import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Geolocation } from '@capacitor/geolocation';
import { KeepAwake } from '@capacitor-community/keep-awake';
import type { BreadcrumbInput } from '@/types/pda-zone';
import { toast } from '@/utils/toast';
import { networkService } from '@/services/network.service';
import { pdaZoneOfflineService } from '../offline/pda-zone.service';
import { isNativePlatform, flushBreadcrumbs } from '@/services/pda-zone-tracking.service';

interface PdaZoneState {
  // GPS tracking
  isTracking: boolean;
  activeZoneId: string | null;
  pointBuffer: BreadcrumbInput[];
  pointCount: number;
  lastAccuracy: number | null;
  watchId: number | null;
  capacitorWatchId: string | null;

  // Actions
  startTracking: (zoneId: string) => void;
  stopTracking: () => void;
  addPoint: (point: BreadcrumbInput) => void;
  flush: () => Promise<void>;
}

const FLUSH_INTERVAL = 30_000; // 30s
const FLUSH_THRESHOLD = 20; // points

let flushTimer: ReturnType<typeof setInterval> | null = null;

export const usePdaZoneStore = create<PdaZoneState>()(
  persist(
    (set, get) => ({
      isTracking: false,
      activeZoneId: null,
      pointBuffer: [],
      pointCount: 0,
      lastAccuracy: null,
      watchId: null,
      capacitorWatchId: null,

      startTracking: (zoneId: string) => {
        const state = get();
        if (state.isTracking) {
          get().stopTracking();
        }

        if (isNativePlatform) {
          // Use Capacitor Geolocation for native platforms
          Geolocation.watchPosition(
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
            (position, err) => {
              if (err || !position) return;
              const point: BreadcrumbInput = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude ?? undefined,
                speed: position.coords.speed ?? undefined,
                heading: position.coords.heading ?? undefined,
                recorded_at: new Date(position.timestamp).toISOString(),
              };
              get().addPoint(point);
            }
          ).then((capWatchId) => {
            set({ capacitorWatchId: capWatchId });
          }).catch((err) => {
            console.error('[PDA Zone] Geolocation watch failed:', err);
            set({ isTracking: false, activeZoneId: null });
          });
        } else {
          // Fallback to browser API
          if (!navigator.geolocation) return;

          const watchId = navigator.geolocation.watchPosition(
            (pos) => {
              const point: BreadcrumbInput = {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
                altitude: pos.coords.altitude ?? undefined,
                speed: pos.coords.speed ?? undefined,
                heading: pos.coords.heading ?? undefined,
                recorded_at: new Date(pos.timestamp).toISOString(),
              };
              get().addPoint(point);
            },
            (error) => {
              console.error('GPS error:', error.message);
              toast.error(`GPS error: ${error.message}`);
            },
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
          );

          set({ watchId });
        }

        if (flushTimer) {
          clearInterval(flushTimer);
        }
        flushTimer = setInterval(() => {
          get().flush();
        }, FLUSH_INTERVAL);

        set({ isTracking: true, activeZoneId: zoneId, pointBuffer: [], pointCount: 0 });

        // Keep screen awake during GPS tracking (critical for field ops)
        try {
          KeepAwake.keepAwake().catch(() => {});
        } catch {
          // Plugin not available on this platform
        }
      },

      stopTracking: () => {
        const state = get();
        const pendingPoints = [...state.pointBuffer];
        const zoneId = state.activeZoneId;

        // Clear watchers
        if (isNativePlatform && state.capacitorWatchId) {
          Geolocation.clearWatch({ id: state.capacitorWatchId });
        } else if (state.watchId !== null) {
          navigator.geolocation.clearWatch(state.watchId);
        }
        if (flushTimer) {
          clearInterval(flushTimer);
          flushTimer = null;
        }

        // Reset state immediately for UI
        set({ isTracking: false, activeZoneId: null, watchId: null, capacitorWatchId: null, pointBuffer: [] });

        // Allow screen to sleep if personnel tracking is also stopped
        try {
          import('./personnel-tracking.store').then(({ usePersonnelTrackingStore }) => {
            if (!usePersonnelTrackingStore.getState().isTracking) {
              KeepAwake.allowSleep().catch(() => {});
            }
          }).catch(() => {});
        } catch {
          // Plugin not available on this platform
        }

        // Flush captured points after state reset — fall back to offline storage on failure
        if (pendingPoints.length > 0 && zoneId) {
          if (!networkService.isOnline()) {
            pdaZoneOfflineService.saveBreadcrumbsOffline(zoneId, pendingPoints).catch(console.error);
          } else {
            flushBreadcrumbs(zoneId, pendingPoints).catch(() => {
              // Online flush failed — save offline so points aren't lost
              pdaZoneOfflineService.saveBreadcrumbsOffline(zoneId, pendingPoints).catch(console.error);
            });
          }
        }
      },

      addPoint: (point: BreadcrumbInput) => {
        // Filter out low-accuracy GPS readings (>50m) to prevent inflated coverage
        if (point.accuracy && point.accuracy > 50) return;

        set((s) => {
          const buffer = [...s.pointBuffer, point];
          const newState: Partial<PdaZoneState> = {
            pointBuffer: buffer,
            pointCount: s.pointCount + 1,
            lastAccuracy: point.accuracy ?? s.lastAccuracy,
          };
          return newState as PdaZoneState;
        });

        if (get().pointBuffer.length >= FLUSH_THRESHOLD) {
          get().flush();
        }
      },

      flush: async () => {
        const { pointBuffer, activeZoneId } = get();
        if (pointBuffer.length === 0 || !activeZoneId) return;

        const points = [...pointBuffer];
        set({ pointBuffer: [] });

        if (!networkService.isOnline()) {
          // Save offline
          try {
            await pdaZoneOfflineService.saveBreadcrumbsOffline(activeZoneId, points);
          } catch {
            // Re-add on failure
            set((s) => ({ pointBuffer: [...points, ...s.pointBuffer] }));
          }
          return;
        }

        try {
          await flushBreadcrumbs(activeZoneId, points);
        } catch {
          // Re-add on failure
          set((s) => ({ pointBuffer: [...points, ...s.pointBuffer] }));
        }
      },
    }),
    {
      name: 'pda-zone-gps-storage',
      partialize: (state) => ({
        activeZoneId: state.activeZoneId,
        pointBuffer: state.pointBuffer,
        pointCount: state.pointCount,
      }),
      onRehydrateStorage: () => (state) => {
        // Flush orphaned points from a crash/restart.
        // isTracking defaults to false (not persisted), so if there are
        // buffered points with an activeZoneId, they need to be flushed.
        if (state && !state.isTracking && state.pointBuffer.length > 0 && state.activeZoneId) {
          const points = [...state.pointBuffer];
          const zoneId = state.activeZoneId;

          // Clear orphaned state immediately
          usePdaZoneStore.setState({ pointBuffer: [], activeZoneId: null, pointCount: 0 });

          // Attempt to sync the orphaned points
          if (networkService.isOnline()) {
            flushBreadcrumbs(zoneId, points)
              .catch(() => {
                pdaZoneOfflineService.saveBreadcrumbsOffline(zoneId, points).catch(console.error);
              });
          } else {
            pdaZoneOfflineService.saveBreadcrumbsOffline(zoneId, points).catch(console.error);
          }
        }
      },
    }
  )
);
